import {
  App,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { inventoryApi, type StoreItem, type StoreItemInput } from '@/api/inventory';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { extractError } from '@/shared/lib/error-message';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

interface FormShape {
  name: string;
  itemTypeId: number;
  classApplicability?: number[];
  sellingPrice: number;
  openingStock: number;
  reorderLevel: number;
}

export function StoreItemsTab() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('INVENTORY:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('INVENTORY:EDIT'));

  const [editing, setEditing] = useState<StoreItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data, isLoading } = useQuery({
    queryKey: ['inventory', 'store', 'items'],
    queryFn: () => inventoryApi.listStoreItems(),
  });

  const { data: types } = useQuery({
    queryKey: ['setup', 'lookups', 'store-item-types', 'active'],
    queryFn: () => setupApi.listLookup('store-item-types', true),
    staleTime: 5 * 60_000,
  });

  const { data: classes } = useQuery({
    queryKey: ['setup', 'classes'],
    queryFn: setupApi.listClasses,
    staleTime: 5 * 60_000,
  });

  const upsert = useMutation({
    mutationFn: (values: FormShape) => {
      const body: StoreItemInput = {
        name: values.name.trim(),
        itemTypeId: values.itemTypeId,
        classApplicability: values.classApplicability,
        sellingPrice: Number(values.sellingPrice).toFixed(2),
        openingStock: Number(values.openingStock).toFixed(2),
        reorderLevel: Number(values.reorderLevel).toFixed(2),
      };
      return editing ? inventoryApi.updateStoreItem(editing.id, body) : inventoryApi.createStoreItem(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Item updated' : 'Item created');
      qc.invalidateQueries({ queryKey: ['inventory', 'store', 'items'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ openingStock: 0, reorderLevel: 5 });
  };
  const openEdit = (row: StoreItem) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      itemTypeId: row.itemTypeId,
      classApplicability: row.classApplicability ?? undefined,
      sellingPrice: Number(row.sellingPrice),
      openingStock: Number(row.openingStock),
      reorderLevel: Number(row.reorderLevel),
    });
  };
  const open = creating || editing != null;

  const columns: TableProps<StoreItem>['columns'] = [
    {
      title: 'Item',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.name}</Typography.Text>
          <Space size={6}>
            {row.itemTypeName && <Tag>{row.itemTypeName}</Tag>}
            {row.lowStock && (
              <Tag color="warning" icon={<WarningOutlined />}>Low stock</Tag>
            )}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Stock',
      dataIndex: 'currentStock',
      width: 100,
      align: 'right',
      render: (s: string, row) => (
        <Tooltip title={`Reorder at ${PKR(row.reorderLevel)}`}>
          <Typography.Text strong>{PKR(s)}</Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: 'Cost (avg)',
      dataIndex: 'costPrice',
      width: 110,
      align: 'right',
      render: (v: string) => `PKR ${PKR(v)}`,
    },
    {
      title: 'Price',
      dataIndex: 'sellingPrice',
      width: 110,
      align: 'right',
      render: (v: string) => `PKR ${PKR(v)}`,
    },
    {
      title: 'Actions',
      width: 110,
      align: 'right',
      render: (_, row) =>
        canEdit ? (
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Space>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New item
          </Button>
        )}
        <Typography.Text type="secondary">
          Cost is recalculated as a weighted average each time you record a purchase.
        </Typography.Text>
      </Space>

      {data && data.length === 0 && !isLoading ? (
        <Empty description="No store items yet" />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={isLoading}
          columns={columns}
          dataSource={data ?? []}
          pagination={{ pageSize: 25 }}
        />
      )}

      <Drawer
        title={editing ? `Edit · ${editing.name}` : 'New store item'}
        open={open}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        size={480}
        destroyOnHidden
        extra={
          <Space>
            <Button
              onClick={() => {
                setEditing(null);
                setCreating(false);
              }}
            >
              Cancel
            </Button>
            <Button type="primary" loading={upsert.isPending} onClick={() => form.submit()}>
              {editing ? 'Save' : 'Create'}
            </Button>
          </Space>
        }
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            <Form.Item label="Name" name="name" rules={[{ required: true, max: 200 }]}>
              <Input autoFocus placeholder="e.g. Class 5 Maths textbook" />
            </Form.Item>
            <Form.Item label="Type" name="itemTypeId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(types ?? []).map((t) => ({ value: t.id, label: t.name }))}
              />
            </Form.Item>
            <Form.Item
              label="Applies to classes"
              name="classApplicability"
              tooltip="Leave empty to apply to all classes / walk-ins."
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                options={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                label="Selling price (PKR)"
                name="sellingPrice"
                rules={[{ required: true, type: 'number', min: 0 }]}
                style={{ flex: 1 }}
              >
                <InputNumber style={{ width: '100%' }} step={50} min={0} />
              </Form.Item>
              <Form.Item
                label="Reorder level"
                name="reorderLevel"
                rules={[{ required: true, type: 'number', min: 0 }]}
                style={{ width: 160 }}
              >
                <InputNumber style={{ width: '100%' }} step={5} min={0} />
              </Form.Item>
            </Space.Compact>
            <Form.Item
              label="Opening stock"
              name="openingStock"
              tooltip="Stock on hand at go-live. Cannot be changed once purchases are recorded."
              rules={[{ required: true, type: 'number', min: 0 }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                step={10}
                min={0}
                disabled={!!editing && Number(editing.currentStock) !== Number(editing.openingStock)}
              />
            </Form.Item>
          </Form>
        )}
      </Drawer>
    </div>
  );
}
